import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Paper,
  IconButton,
  Typography,
} from '@material-ui/core';
import {
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
} from '@material-ui/icons';
import { useLocation } from 'react-router-dom';
import { useTimelineViewStyles } from './styles/timeline-view.styles';
import { UrlHelper } from '../../utils/url-helper';
import { useTimelineData } from './hooks/useTimelineData';
import {
  TimeMarkersRow,
  MilestonesRow,
  HorizontalPipelineLayout,
} from './components/TimelineComponents';
import { getFullDateTimeLabel } from './utils/timeline-processing';
import { ProcessedMilestone } from './types';
import { MilestoneTimeDiff } from './components/MilestoneTimeDiff';
import { CustomAlert, LoadingSkeleton, TimelineLegend } from './components/TimelineUtilityComponents';

export const TimelineView = React.memo((): JSX.Element => {
  const classes = useTimelineViewStyles();
  const location = useLocation();
  const website = UrlHelper.getParamValue('website');
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  
  // Milestone selection state
  const [selectedMilestones, setSelectedMilestones] = useState<ProcessedMilestone[]>([]);
  
  const {
    data: timelineData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    dataUpdatedAt,
    timeMarkers,
    processedMilestones,
    processedPipelines,
    timeRange,
  } = useTimelineData(website || '');

  // Auto-scroll to the right end of the timeline on first load with smooth animation
  useEffect(() => {
    if (!isLoading && processedPipelines.length > 0 && timelineScrollRef.current) {
      // Use setTimeout to ensure the DOM is fully rendered
      setTimeout(() => {
        if (timelineScrollRef.current) {
          timelineScrollRef.current.scrollTo({
            left: timelineScrollRef.current.scrollWidth,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [isLoading, processedPipelines.length, timelineData]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Milestone selection handlers
  const handleMilestoneClick = useCallback((milestone: ProcessedMilestone) => {
    setSelectedMilestones(prev => {
      if (prev.length === 0) {
        return [milestone];
      } else if (prev.length === 1) {
        return [prev[0], milestone];
      } else {
        // Replace the first milestone with the new one
        return [prev[1], milestone];
      }
    });
  }, []);

  const clearMilestoneSelection = useCallback(() => {
    setSelectedMilestones([]);
  }, []);

  if (isError) {
    return (
      <Paper className={classes.paper}>
        <div className={classes.header}>
          <div className={classes.headerContent}>
            <ErrorIcon className={classes.headerIcon} color="error" />
            <Typography variant="h4" className={classes.headerTitle}>
               Timeline and Status View for Website
            </Typography>
          </div>
          <IconButton size="small" onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        </div>
        <CustomAlert 
          severity="error" 
          action={
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          }
        >
          Failed to load milestones: {error instanceof Error ? error.message : String(error) || 'Unknown error'}
        </CustomAlert>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerContent}>
            <TimelineIcon className={classes.headerIcon} />
            <Typography variant="h4" className={classes.headerTitle}>
                 Timeline and Status View for Website
              </Typography>
            {!isLoading && processedMilestones.length > 0 && (
              <span style={{ marginLeft: 8, fontSize: '1rem', color: '#666' }}>
                {getFullDateTimeLabel(timeRange.start)} - {getFullDateTimeLabel(timeRange.end)}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MilestoneTimeDiff 
              selectedMilestones={selectedMilestones}
              onClear={clearMilestoneSelection}
            />
            {isFetching && <div>Loading...</div>}
            <IconButton size="small" onClick={handleRefresh} disabled={isFetching}>
              <RefreshIcon />
            </IconButton>
          </div>
        </div>

        {/* Legend - Just below header */}
        <TimelineLegend />

        {(
          <>
            {isLoading ? (
              <LoadingSkeleton />
            ) : !processedMilestones.length ? (
              <div className={classes.content}>
                <CustomAlert severity="info">
                  No milestones available for this Website
                </CustomAlert>
              </div>
            ) : <>
              <div className={classes.content}>
                {/* Unified Timeline Container - Single scrollable wrapper */}
                <div 
                  ref={timelineScrollRef}
                  style={{
                    position: 'relative',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    minWidth: '100%',
                    border: '1px solid #e0e0e0',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    padding: '16px'
                  }}
                >
                  <div style={{
                    minWidth: processedPipelines.length > 0 ? `${Math.max(processedPipelines.length * 300, 1200)}px` : '100%',
                    width: '100%',
                    position: 'relative'
                  }}>
                    
                    {/* Milestone Lines - positioned to extend through entire timeline */}
                    {processedMilestones.map((milestone, index) => (
                      <div
                        key={`milestone-line-${index}`}
                        style={{
                          position: 'absolute',
                          left: `${milestone.position}%`,
                          top: '34px', // Start right after the milestone chip (24px height + 10px top margin)
                          width: '2px',
                          height: 'calc(100% - 34px)',
                          backgroundColor: 'transparent',
                          borderLeft: `2px dashed ${milestone.color}`,
                          opacity: 0.3,
                          transform: 'translateX(-50%)',
                          zIndex: 50,
                          pointerEvents: 'none'
                        }}
                      />
                    ))}
                    
                    {/* Milestones Row */}
                    <MilestonesRow 
                      milestones={processedMilestones} 
                      timeMarkers={timeMarkers}
                      pipelines={processedPipelines}
                      onMilestoneClick={handleMilestoneClick}
                      selectedMilestones={selectedMilestones}
                    />
                    
                    {/* Pipeline-aligned Time Markers */}
                    <TimeMarkersRow timeMarkers={timeMarkers} pipelines={processedPipelines} />

                    {/* Horizontal Pipeline Layout */}
                    {processedPipelines.length > 0 && (
                      <HorizontalPipelineLayout 
                        pipelines={processedPipelines} 
                        timeMarkers={timeMarkers} 
                      />
                    )}
                  </div>
                </div>
              </div>
            </>}
          </>
        )}
    </Paper>
  );
});

TimelineView.displayName = 'TimelineView';
